import { useState, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const FAILURE_REPLY =
  'I could not connect just now. Please tap "Continue on WhatsApp" below to reach Nitika directly.';

/**
 * Owns chat conversation state and the /api/chat network call.
 * Callers get a clean read-only snapshot + a `sendUserMessage` function.
 */
export const useChat = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const sessionIdRef = useRef(null);
  const initedRef = useRef(false);

  const postToBackend = useCallback(async (nextMessages) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          messages: nextMessages
        })
      });
      if (!res.ok) throw new Error('Chat service unavailable');
      const data = await res.json();
      sessionIdRef.current = data.session_id;
      setMessages([...nextMessages, { role: 'assistant', content: data.reply }]);
      setIsDone(!!data.is_done);
    } catch (_err) {
      setMessages([...nextMessages, { role: 'assistant', content: FAILURE_REPLY }]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const initChat = useCallback(async () => {
    if (initedRef.current) return;
    initedRef.current = true;
    await postToBackend([]);
  }, [postToBackend]);

  const sendUserMessage = useCallback(
    async (text) => {
      const trimmed = (text || '').trim();
      if (!trimmed) return;
      const next = [...messages, { role: 'user', content: trimmed }];
      setMessages(next);
      await postToBackend(next);
    },
    [messages, postToBackend]
  );

  return { messages, isLoading, isDone, initChat, sendUserMessage };
};
